import { Formik } from "formik";
import { useRef, useState } from "react";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormTextArea from "../formFields/CMSFormTextArea";
import { reviewSchema } from "../utils/validations";
import GeneralButton from "../common/GeneralButton";
import ContainerLoading from "../common/loading/ContainerLoading";
import { motion } from "framer-motion";
import success from "../../assets/images/success.png";
import { useToast } from "../contexts/ToastContext";
import CMSFormImage from "../formFields/CMSFormImage";
import axios from "../../config/axios";

const ReviewSubmissionForm = () => {
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "success" | "error"
  const { addToast } = useToast();
  const [error, setError] = useState("");
  const refs = {
    name: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    image: useRef(null),
    message: useRef(null),
  };

  // 🔹 Scroll to first error field
  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });
    setTimeout(() => el.focus?.({ preventScroll: true }), 500);
  };

  // 🔹 Handle form submission (moved outside Formik for clarity)
  const handleSubmitReview = async (values, resetForm) => {
    setStatus("loading");

    try {
      const formData = new FormData();
      for (let name in values) {
        if (Array.isArray(values[name])) {
          formData.append(name, JSON.stringify(values[name]));
        } else {
          formData.append(name, values[name]);
        }
      }
      if (values.image) {
        await axios.post("/testimonials", formData);
      } else {
        await axios.post("/testimonials", values);
      }

      setStatus("success");
      addToast("Review submitted successfully!", "success");

      // Reset form after 5 seconds
      setTimeout(() => {
        resetForm();
        setStatus("idle");
      }, 10000);
    } catch (err) {
      setStatus("idle");
      if (err.status === 409) {
        setError("You’ve already submitted a review.");
      } else {
        setError("Review submission failed. Please try again.");
      }
        setTimeout(() => {
        setError("");
      }, 5000);
    }
  };

  return (
    <>
      <div
        className={`${
          status === "success" ? "bg-secondary" : "bg-[#DEE2DD]/50"
        }  px-10 2xl:px-20 py-8 2xl:py-10  rounded-[15px] relative min-h-[300px]`}
      >
        {status === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center rounded-[15px] z-10">
            <ContainerLoading />
          </div>
        )}
        {status != "success" && (
          <p className="text-34pt lg:text-36pt pb-8 2xl:pb-10">Add Your Review</p>
        )}
        {status === "success" && (
          <motion.div
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -200, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          >
            <div className="grid grid-cols-1 gap-8 items-start ">
              <div>
                <div className="w-full rounded-[15px] p-0 ">
                  <div className="grid grid-cols-1  gap-3 md:gap-4 2.5xl:gap-10 mt-0">
                    <div className="rounded-[15px]  flex flex-col justify-between order-2 ">
                      <div className="w-full">
                        <p className="text-white text-28pt md:text-48pt leading-none mb-4 2xl:mb-8 text-center ">
                          Review Submission Successful
                        </p>
                        <p className="text-orange text-[25px] md:text-[34px] leading-none text-center mb-8">
                          Thank you for taking the time to share your experience. We truly value your feedback and appreciate your input.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center justify-center order-1 mb-3">
                      <img
                        src={success}
                        alt="success"
                        className="w-[100px] h-[100px] "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {status !== "success" && (
          <Formik
            initialValues={{
              name: "",
              email: "",
              mobile: "",
              image: "",
              message: "",
            }}
            validationSchema={reviewSchema}
            onSubmit={(values, { resetForm }) =>
              handleSubmitReview(values, resetForm)
            }
          >
            {(form) => (
              <div className="w-full grid grid-cols-1 gap-8">
                <div className="grid grid-cols-1  gap-8">
                  <CMSFormInput
                    name="name"
                    label="Full Name"
                    className="1"
                    form={form}
                    ref={refs.name}
                    required
                  />
                  <CMSFormInput
                    name="email"
                    label="Email"
                    form={form}
                    ref={refs.email}
                    className="1"
                    required
                  />
                </div>

                <div className="grid grid-cols-1  gap-8">
                  <CMSFormTelephone
                    name="mobile"
                    label="Phone"
                    form={form}
                    ref={refs.phone}
                    required
                  />
                  <CMSFormImage
                    name="image"
                    label="Image"
                    form={form}
                    ref={refs.image}
                    required={true}
                    helpMessage={{ message: "Upload the logo in png format." }}
                    moduleName="clients"
                  />
                </div>

                <CMSFormTextArea
                  name="message"
                  label="Message"
                  maxLength={300}
                  form={form}
                  ref={refs.message}
                  required
                />
                {error && (
                  <div
                    className={`relative transition-all duration-300 ease-in-out my-3 ${
                      error
                        ? "opacity-100 -translate-y-3"
                        : "opacity-0 -translate-y-2"
                    }`}
                  >
                    {error && <span className="errorMessage">{error}</span>}
                  </div>
                )}
                <div className="flex items-end justify-end">
                  <GeneralButton
                    text="Submit"
                    type="submit"
                    title="Click to submit review"
                    className="text-lg px-4"
                    // textColor="#fff"
                    onClick={() => {
                      form.handleSubmit();
                      setTimeout(() => scrollToFirstError(form?.errors), 100);
                    }}
                  />
                </div>
              </div>
            )}
          </Formik>
        )}
      </div>
    </>
  );
};

export default ReviewSubmissionForm;
